import { useState, useEffect, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Eye,
  EyeSlash as EyeOff,
  Scales as Scale,
  ArrowRight,
  CircleNotch as Loader2,
  ShieldCheck,
  CalendarBlank as Calendar,
  Gavel,
  GoogleLogo,
  MicrosoftOutlookLogo,
  Buildings,
  UserPlus,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useEstablishSession, useLogin } from '@/features/auth/hooks';
import { MfaChallengeStep } from '@/components/auth/MfaChallengeStep';
import { isValidEmail, EMAIL_ERROR } from '@/lib/validators';
import { peekSignOutNotice, clearSignOutNotice } from '@/lib/signOutNotice';
import { beginAuthentication, fetchAuthProviders } from '@/lib/authJourney';

/**
 * Map the `?error=` code the OAuth begin/callback redirects back with into a message the user can
 * actually act on. Without this the login page silently swallowed the code — so a failed
 * "Continue with Google" (e.g. the provider isn't configured on the server) looked like a dead
 * button. Coded failures get friendly copy; the identity resolver's own messages (already
 * user-facing, e.g. "No Jurivoxa account uses this email") are shown as-is.
 */
function oauthErrorMessage(code: string | null): string | null {
  if (!code) return null;
  switch (code) {
    case 'provider_not_configured':
      return 'Google / Microsoft sign-in isn\'t configured on this server yet. Sign in with your email and password, or ask your administrator to enable it.';
    case 'unsupported_provider':
      return 'That sign-in method isn\'t supported.';
    case 'missing_params':
      return 'Sign-in was interrupted before it finished. Please try again.';
    case 'email_not_verified':
      return 'We couldn\'t start your account because your provider hasn\'t verified that email address. Try a different account, or sign up with your email instead.';
    default:
      // Resolver messages arrive URL-encoded and are already written for the user.
      try { return decodeURIComponent(code); } catch { return 'Sign-in failed. Please try again.'; }
  }
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const oauthError = oauthErrorMessage(searchParams.get('error'));
  // Why this tab was signed out (e.g. the account was deactivated mid-session). Read once during
  // render with peek — NOT a read-and-remove, which React StrictMode's double-invoked initialiser
  // would swallow — then cleared by the effect below so a later visit to /login does not repeat it.
  const [signOutNotice] = useState(() => peekSignOutNotice());
  useEffect(() => {
    if (signOutNotice) clearSignOutNotice();
  }, [signOutNotice]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  // Consecutive failed sign-ins. From the 3rd failure we surface a GENERIC hint (shown to anyone who
  // fails repeatedly — never account-specific, so no enumeration leak) pointing an admin-reset user
  // at their email. A single typo on the first attempts stays quiet.
  const [failedAttempts, setFailedAttempts] = useState(0);
  /**
   * The live MFA challenge, if the password step returned one.
   *
   * Component state deliberately — NOT the auth store and NOT the URL. It is a five-minute
   * credential; it must not survive a reload, and it must not be readable from history or from
   * anything that logs a URL.
   */
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  /** Why the person was sent back to the password step, when they were. */
  const [challengeNotice, setChallengeNotice] = useState<string | null>(null);

  const establishSession = useEstablishSession();
  const login = useLogin((token) => {
    setChallengeNotice(null);
    setChallengeToken(token);
  });

  // Only offer a social-login button for providers the server has actually configured — otherwise
  // the button dead-ends, bouncing straight back with "not configured". Defaults to none until the
  // check resolves, so no button flashes then vanishes.
  const {
    data: providers,
    isError: providersFailed,
    refetch: refetchProviders,
  } = useQuery({
    queryKey: ['auth', 'providers'],
    queryFn: fetchAuthProviders,
    staleTime: 5 * 60 * 1000,
  });
  const hasGoogle = providers?.google ?? false;
  const hasMicrosoft = providers?.microsoft ?? false;
  const hasSocialLogin = hasGoogle || hasMicrosoft;

  // Live email validation — runs on blur and (once touched) while typing.
  const validateEmail = (value: string) =>
    setEmailError(isValidEmail(value) ? '' : EMAIL_ERROR);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (login.isPending) return;
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_ERROR);
      return;
    }
    setEmailError('');
    setChallengeNotice(null);
    login.mutate(
      { email, password, rememberMe },
      {
        onError: () => setFailedAttempts((n) => n + 1),
        onSuccess: () => setFailedAttempts(0),
      },
    );
  };

  /**
   * Abandon the challenge and return to email + password.
   *
   * The password is cleared as well as the token: the person is starting the sign-in over, and
   * leaving a filled password box behind a "sign in as someone else" link is how the wrong
   * credentials get submitted for the right email.
   */
  const restartSignIn = (reason?: string) => {
    setChallengeToken(null);
    setPassword('');
    setChallengeNotice(reason ?? null);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
        {/* Left — dark brand panel */}
        <div className="hidden flex-col justify-between bg-ink-900 p-12 text-ink-200 lg:flex">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-600 text-white">
              <Scale className="h-5 w-5" weight="duotone" />
            </div>
            <div className="text-lg font-semibold tracking-tight text-white">Jurivoxa</div>
          </Link>

          <div className="max-w-lg">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-brand-400">
              For barristers, by design
            </div>
            <h1 className="mb-5 text-[34px] font-semibold leading-[1.15] tracking-tight text-white xl:text-[40px]">
              The operating system for the modern barrister.
            </h1>
            <p className="max-w-md text-[15px] leading-relaxed text-ink-300">
              Run your practice yourself — every matter, hearing, judgement, fee
              and document at your fingertips. Invite a clerk to balance the
              load when you want one.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-ink-800 pt-8">
              {[
                { icon: <Gavel className="h-4 w-4" />, label: 'Hearings & lists' },
                { icon: <Calendar className="h-4 w-4" />, label: 'Diary & clashes' },
                { icon: <ShieldCheck className="h-4 w-4" />, label: 'BSB compliant' },
              ].map((f) => (
                <div key={f.label} className="flex flex-col gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-ink-800 text-brand-400">
                    {f.icon}
                  </div>
                  <div className="text-xs text-ink-300">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-ink-500">
            © {new Date().getFullYear()} Jurivoxa · Built for the Bar
          </div>
        </div>

        {/* Right — form, or the second factor when the password step asked for one */}
        <div className="flex items-center justify-center p-6 sm:p-12">
          {challengeToken ? (
            /*
              The whole column is replaced, not merely disabled behind an overlay. The password
              step is finished; leaving its fields on screen invites a second submit against a
              request that has already been answered.
            */
            <MfaChallengeStep
              challengeToken={challengeToken}
              email={email}
              onAuthenticated={establishSession}
              onRestart={restartSignIn}
            />
          ) : (
          <div className="w-full max-w-md">
            {/* A LINK, like its counterpart in the dark panel (:141). That panel is `lg:flex`, so
                below 1024px it is hidden and this mark was the only logo on screen — as a plain
                div it left a visitor on a phone with NO route back to the public site. Same
                destination and same affordance at every width. */}
            <Link to="/" className="mb-8 flex items-center gap-3 lg:hidden">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-600 text-white">
                <Scale className="h-5 w-5" weight="duotone" />
              </div>
              <div className="text-lg font-semibold tracking-tight text-foreground">Jurivoxa</div>
            </Link>

            <div className="mb-6">
              <h2 className="mb-1 text-[24px] font-semibold tracking-tight text-foreground">
                Sign in to your account
              </h2>
              <p className="text-sm text-muted-foreground">
                Welcome back. Enter your details to continue.
              </p>
            </div>

            {/*
              Why the second-factor step handed them back. Without this an expired challenge looks
              like the app simply forgot what they were doing.
            */}
            {challengeNotice && (
              <div
                role="status"
                className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400"
              >
                {challengeNotice}
              </div>
            )}

            {/* Explain an involuntary sign-out (e.g. the account was deactivated mid-session) instead
                of dropping the person on an unexplained login screen. */}
            {signOutNotice && (
              <div
                role="status"
                className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400"
              >
                {signOutNotice}
              </div>
            )}

            {/* Surface an OAuth failure returned via ?error= so a failed provider sign-in is never
                silent (it used to bounce back here showing nothing). */}
            {oauthError && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400"
              >
                {oauthError}
              </div>
            )}

            {/* Continue with a provider — the UI begins an authentication JOURNEY (sign_in),
                it does not think about OAuth. Intent rides in the OAuth state. Each button renders
                only when the server reports that provider as configured, so there are no dead
                buttons; when none are enabled the whole block (and the "or" divider) is omitted and
                email/password becomes the sole, uncluttered sign-in. */}
            {hasSocialLogin && (
              <>
                <div className="space-y-2.5">
                  {hasGoogle && (
                    <button
                      type="button"
                      onClick={() => beginAuthentication({ provider: 'google', intent: 'sign_in' })}
                      className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded-md border border-border bg-card px-5 text-sm font-medium text-foreground transition hover:bg-muted/40"
                    >
                      <GoogleLogo className="h-[18px] w-[18px]" weight="bold" />
                      Continue with Google
                    </button>
                  )}
                  {hasMicrosoft && (
                    <button
                      type="button"
                      onClick={() => beginAuthentication({ provider: 'microsoft', intent: 'sign_in' })}
                      className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded-md border border-border bg-card px-5 text-sm font-medium text-foreground transition hover:bg-muted/40"
                    >
                      <MicrosoftOutlookLogo className="h-[18px] w-[18px]" weight="bold" />
                      Continue with Microsoft
                    </button>
                  )}
                </div>

                <div className="my-5 flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              </>
            )}

            {/* A failed providers fetch reports every provider as "not configured", so the block
                above renders nothing and someone who signs in with Google sees only email and
                password — reading as "my sign-in was taken away". No fake button is drawn (that
                would be the dead button this page exists to avoid); it says what happened. */}
            {providersFailed && (
              <div
                role="alert"
                className="mb-5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
              >
                Couldn't check which sign-in options are available. If you normally continue with
                Google or Microsoft,{' '}
                <button type="button" onClick={() => void refetchProviders()} className="font-semibold underline">
                  try again
                </button>
                .
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  maxLength={255}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (emailTouched) validateEmail(e.target.value); }}
                  onBlur={(e) => { setEmailTouched(true); validateEmail(e.target.value); }}
                  className="h-11 w-full rounded-md border border-border bg-card px-3.5 text-sm text-foreground transition placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  placeholder="you@chambers.co.uk"
                />
                {emailError && <p className="mt-1.5 text-sm text-red-700 dark:text-red-300">{emailError}</p>}
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium text-foreground">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 w-full rounded-md border border-border bg-card px-3.5 pr-10 text-sm text-foreground transition placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-100"
                />
                Remember me on this device
              </label>

              <button
                type="submit"
                disabled={login.isPending}
                className="group inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {login.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>

              {/* After repeated failures, nudge the (possibly admin-reset) user toward their email.
                  Generic wording only — identical for every account, so it reveals nothing. */}
              {failedAttempts >= 3 && (
                <div
                  role="status"
                  className="rounded-md border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400"
                >
                  Still not working? If an administrator recently reset your password, check your email
                  for the new details — or contact your admin.
                </div>
              )}
            </form>

            {/* Journey entries — equal weight to the auth options, not footer links (design §1).
                Each leads to its own journey that stamps the correct intent. */}
            <div className="mt-6 space-y-2.5 border-t border-border pt-6">
              <Link
                to="/onboarding"
                className="group flex items-center gap-3 rounded-md border border-border bg-card px-4 py-3 transition hover:bg-muted/40"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:text-brand-400">
                  <Buildings className="h-[18px] w-[18px]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground">New to Jurivoxa?</div>
                  <div className="text-xs text-muted-foreground">Create a workspace</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-foreground" />
              </Link>
              <Link
                to="/join-workspace"
                className="group flex items-center gap-3 rounded-md border border-border bg-card px-4 py-3 transition hover:bg-muted/40"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:text-brand-400">
                  <UserPlus className="h-[18px] w-[18px]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground">Invited to a chambers?</div>
                  <div className="text-xs text-muted-foreground">Join a workspace</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-foreground" />
              </Link>
            </div>

            {import.meta.env.DEV && (
              <div className="mt-6 rounded-md border border-dashed border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                <div className="mb-1 font-semibold text-foreground">Dev test accounts</div>
                <div className="space-y-0.5">
                  <div>admin@chambers.test (admin)</div>
                  <div>clerk@chambers.test (senior clerk)</div>
                  <div>thornton.kc@chambers.test (silk)</div>
                  <div>cross@chambers.test (junior)</div>
                  <div className="pt-1">password: ChangeMe123!</div>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
